import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, CheckCircle2, Loader2, X, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة

const WorkflowHistoryModal = ({ module, refId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🎯 إعدادات الترجمة والاتجاه
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const fontClass = isRTL ? 'font-cairo' : 'font-sans';

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/workflow/history/${module}/${refId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) {
        console.error("Failed to load history", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [module, refId]);

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300 ${fontClass}`}
      dir={isRTL ? 'rtl' : 'ltr'} // 🎯 قلب الواجهة
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh] overflow-hidden animate-in zoom-in-95 duration-300">
        
        {/* Header */}
        <div className="p-5 border-b flex justify-between items-center bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-800 leading-none">{t('workflowHistory.title')}</h2>
              <p className="text-[10px] text-slate-400 font-bold mt-1 uppercase tracking-tighter">
                {t('workflowHistory.trackingId')} {refId}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-all text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-white">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="animate-spin text-blue-600" size={40} />
              <p className="text-xs font-bold text-slate-400 uppercase">{t('workflowHistory.fetching')}</p>
            </div>
          ) : (
            <>
              {/* 📍 الموقف الحالي */}
              {data?.pending?.length > 0 && (
                <div className={`mb-10 p-5 bg-gradient-to-${isRTL ? 'l' : 'r'} from-orange-50 to-white border-orange-500 shadow-sm border border-orange-100 ${isRTL ? 'border-r-4 rounded-l-xl' : 'border-l-4 rounded-r-xl'}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle size={14} className="text-orange-600" />
                    <h3 className="text-[11px] font-black uppercase text-orange-600 tracking-widest">{t('workflowHistory.currentTask')}</h3>
                  </div>
                  {data.pending.map((p, i) => (
                    <div key={i} className="flex justify-between items-center bg-white p-3 rounded-lg border border-orange-200/50 shadow-inner">
                      <span className="text-sm font-black text-slate-700">{p.step}</span>
                      <div className={`flex flex-col ${isRTL ? 'items-start' : 'items-end'}`}>
                        <span className="text-[10px] bg-orange-500 text-white px-3 py-1 rounded-full font-black shadow-sm">
                          {p.assignee}
                        </span>
                        <span className="text-[9px] text-slate-400 mt-1 font-bold">
                          {t('workflowHistory.since')} {new Date(p.assigned_on).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 📜 التايملاين */}
              <div className={`space-y-8 relative ${isRTL ? 'mr-3' : 'ml-3'}`}>
                {/* 🎯 خط التايملاين بنحركه يمين وشمال حسب اللغة */}
                <div className={`absolute top-2 bottom-2 w-[3px] bg-slate-100 rounded-full ${isRTL ? 'right-[13px]' : 'left-[13px]'}`}></div>
                
                {data?.history?.length > 0 ? data.history.map((h, i) => (
                  <div key={i} className="flex gap-6 relative z-10">
                    <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center text-white ring-4 ring-white shadow-md shrink-0">
                      <CheckCircle2 size={16} />
                    </div>
                    <div className="flex-1 pb-2 border-b border-slate-50">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="text-sm font-black text-slate-800">{h.step}</h4>
                        <span className="text-[10px] text-slate-400 font-bold bg-slate-50 px-2 py-1 rounded border">
                          {new Date(h.date).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded font-black uppercase text-[9px] border border-green-100">
                          {h.action}
                        </span>
                        <span className="text-slate-400 font-bold">{t('workflowHistory.by')}</span>
                        <span className="text-blue-600 font-black">{h.user}</span>
                      </div>
                      {h.memo && (
                        <div className={`mt-3 text-[11px] text-slate-600 bg-slate-50 p-3 rounded-xl border-slate-300 italic shadow-sm ${isRTL ? 'border-r-4' : 'border-l-4'}`}>
                          "{h.memo}"
                        </div>
                      )}
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-20 relative z-10">
                    <p className="text-slate-300 font-black text-sm uppercase">{t('workflowHistory.noHistory')}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t flex justify-end">
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-slate-800 text-white rounded-lg text-xs font-black hover:bg-slate-700 transition-all shadow-lg active:scale-95 uppercase"
          >
            {t('workflowHistory.closeBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowHistoryModal;